/**
 * motion_dust — velocity dust in a wrapped box around the ship (report §9 R7).
 *
 * A black void with stars at infinity gives no speed cue at all: 100 m/s and 1,000 m/s look the same. A few
 * hundred faint motes fixed in the WORLD, living in a DUST_BOX_M cube that wraps around the ship, give the
 * parallax back. Each mote's seat is hashed once in [0, L)³ (space_look.dustSeat); the vertex stage places it at
 * `(fract((p − camMod)/L) − ½)·L` from the box's centre, where camMod is the ABSOLUTE position of that centre modulo
 * L computed in f64 on the CPU (so the field never jumps at a 25 km floating-origin rebase), and draws it as a
 * streak from its position back along the relative velocity × a 1/30 s shutter, a couple of pixels wide, faded by
 * speed (dustAlpha — invisible parked, gone at hyper), at the box's edge and against the lens.
 * The mesh rides the SHIP (the caller keeps it a follower; in the cockpit that is the camera); one draw.
 *
 * THE STREAK IS BUILT IN VIEW SPACE (space_look.dustStreakView is its CPU mirror; the dust bed compares the two):
 * the tail is clamped by the mote's distance from the CAMERA, the segment is cut at a plane in front of the eye and
 * dropped when it lies wholly behind it, and only then are its two ends projected. See that function for the WHY.
 */
import * as THREE from 'three/webgpu';
import {
  Fn, uniform, vec2, vec3, vec4, float, fract, attribute, cameraProjectionMatrix, modelViewMatrix, positionGeometry,
  screenSize, length, normalize, smoothstep, clamp, max, min, mix, step, varyingProperty,
} from 'three/tsl';
import { markMrtOverlay } from 'enginex/render/post/mrtOverlay.js';
import {
  DUST_BOX_M, DUST_SHUTTER_S, DUST_TAIL_FRAC, DUST_CLIP_M, DUST_NEAR_FADE_M, DUST_EDGE_FADE, DUST_HALF_WIDTH_PX, DUST_GAIN, dustSeat,
} from './space_look.js';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type N = any;

const uCamMod = uniform(new THREE.Vector3());
const uVel = uniform(new THREE.Vector3());
const uAlpha = uniform(0);
const uTint = uniform(new THREE.Color(0.55, 0.6, 0.68));

export const dustState = { count: 0, alpha: 0, speed: 0 };

let mesh: THREE.Mesh | null = null;
/** Build (once per session per count) the mote field. */
export function motionDust(count: number): THREE.Mesh {
  if (mesh && dustState.count === count) return mesh;
  const L = DUST_BOX_M;
  const seats = new Float32Array(count * 4);
  for (let i = 0; i < count; i++) seats.set(dustSeat(i, L), i * 4);
  const quad = new THREE.PlaneGeometry(1, 1);
  const geo = new THREE.InstancedBufferGeometry();
  geo.setIndex(quad.getIndex());
  geo.setAttribute('position', quad.getAttribute('position'));
  geo.setAttribute('uv', quad.getAttribute('uv'));
  geo.setAttribute('aSeat', new THREE.InstancedBufferAttribute(seats, 4));
  geo.instanceCount = count;
  geo.userData.shared = true;
  // WHY DoubleSide: a streak is a line of light on the screen — it has no back. The old quad's frame was left-handed, so
  // every proper streak was back-face culled and only the ones the projection had turned inside out were ever drawn.
  const mat = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide });
  mat.forceSinglePass = true; // a transparent two-sided material is otherwise drawn twice (back, then front)
  markMrtOverlay(mat);
  mat.userData.shared = true;
  mat.name = 'space-dust';
  const seat = attribute('aSeat', 'vec4');
  // The mote's offset from the box's centre, wrapped into the box (the mesh sits on that centre).
  const local = (fract(seat.xyz.sub(uCamMod).div(L)).sub(0.5)).mul(L);
  const vFade = varyingProperty('float', 'vDustFade');
  mat.vertexNode = Fn(() => {
    // Every shared value is a var at the top and every choice is arithmetic (step / mix), never a branch: a value first
    // built inside one branch of a select is unassigned in the other (THE PLACEMENT LAW).
    const hv = (modelViewMatrix.mul(vec4(local, 1.0)) as N).xyz.toVar(); // the mote now, in view space
    // Where the (world-fixed) mote sat a shutter ago, relative to the camera — never more than DUST_TAIL_FRAC of its
    // distance from the CAMERA (the old clamp measured from the box's centre: the ship, in a chase view).
    const sv = (modelViewMatrix.mul(vec4((uVel as N).mul(DUST_SHUTTER_S), 0.0)) as N).xyz.toVar();
    const k = min(float(1), length(hv).mul(DUST_TAIL_FRAC).div(max(length(sv), float(1e-3))));
    const tv = hv.add(sv.mul(k)).toVar();
    // WHY the cut: an end behind the camera has w ≤ 0; projected on its own it drew the streak as a line through the
    // velocity's vanishing point to the far edge of the frame. Cut the segment at a plane in front of the eye FIRST.
    const zc = float(-DUST_CLIP_M);
    const hb = step(zc, hv.z).toVar(); // 1 = this end is behind the cut plane
    const tb = step(zc, tv.z).toVar();
    const dz = tv.z.sub(hv.z).toVar();
    const u = clamp(zc.sub(hv.z).div(dz.add(step(float(0), dz).mul(2e-4).sub(1e-4))), 0, 1).toVar();
    const cut = mix(hv, tv, u).toVar();
    const hC = mix(hv, cut, hb).toVar();
    const tC = mix(tv, cut, tb).toVar();
    const gone = hb.mul(tb); // wholly behind the plane: not drawn
    const head = cameraProjectionMatrix.mul(vec4(hC, 1.0)).toVar();
    const tail = cameraProjectionMatrix.mul(vec4(tC, 1.0)).toVar();
    const wMin = float(DUST_CLIP_M * 0.5); // only ever reached by a streak that is `gone`
    const hN = head.xy.div(max(head.w, wMin)).toVar();
    const tN = tail.xy.div(max(tail.w, wMin)).toVar();
    const g = positionGeometry.xy;       // [-0.5, 0.5]²
    const t = g.y.add(0.5).toVar();      // 0 at the head, 1 at the tail
    const half = screenSize.mul(0.5);    // pixels per NDC unit
    const dirPx = normalize(tN.sub(hN).mul(half).add(vec2(1e-4, 0))).toVar();
    // across = the direction turned −90°: (across, along) is right-handed, so the quad keeps the plane's winding
    const across = vec2(dirPx.y, dirPx.x.negate()).mul(g.x.mul(2 * DUST_HALF_WIDTH_PX)).div(half);
    const w = mix(head.w, tail.w, t).toVar();
    // The fades, per vertex: speed × the box's edge × the lens × the comet's taper × the mote's own brightness.
    const edge = float(1).sub(smoothstep(L * DUST_EDGE_FADE[0], L * DUST_EDGE_FADE[1], length(local)));
    const near = smoothstep(DUST_NEAR_FADE_M[0], DUST_NEAR_FADE_M[1], length(mix(hC, tC, t)));
    const s = mix(hb.mul(u), mix(float(1), u, tb), t); // this vertex's place along the UNCUT streak (0 head … 1 tail)
    vFade.assign(uAlpha.mul(edge).mul(near).mul(float(1).sub(s).mul(0.7).add(0.3)).mul(seat.w));
    const pos = vec4(mix(hN, tN, t).add(across).mul(w), mix(head.z, tail.z, t), w);
    return mix(pos, vec4(2, 2, 2, 1), gone); // outside the clip volume: all four corners on one point
  })();
  mat.colorNode = vec3(uTint).mul(DUST_GAIN);
  mat.opacityNode = clamp(vFade, 0, 1);
  mesh = new THREE.Mesh(geo, mat);
  mesh.frustumCulled = false;
  mesh.name = 'space-dust';
  mesh.renderOrder = 5;
  dustState.count = count;
  return mesh;
}

/** A probe's hold: the dust drawn at this fade and velocity whatever the caller drives (dev levers only; null = live). */
let hold: { alpha: number; vel: THREE.Vector3 } | null = null;
export function holdDust(alpha: number | null, vel?: THREE.Vector3): void {
  hold = alpha === null ? null : { alpha, vel: vel ? vel.clone() : new THREE.Vector3() };
}

/** Per frame: the box centre's ABSOLUTE position (f64 — reduced mod L here), the relative velocity and the fade. */
export function driveDust(camAbs: { x: number; y: number; z: number }, vel: THREE.Vector3, alpha: number): void {
  if (hold) { vel = hold.vel; alpha = hold.alpha; }
  const L = DUST_BOX_M;
  const m = (v: number): number => ((v % L) + L) % L;
  (uCamMod as unknown as { value: THREE.Vector3 }).value.set(m(camAbs.x), m(camAbs.y), m(camAbs.z));
  (uVel as unknown as { value: THREE.Vector3 }).value.copy(vel);
  (uAlpha as unknown as { value: number }).value = alpha;
  dustState.alpha = alpha; dustState.speed = vel.length();
  if (mesh) mesh.visible = alpha > 0.005;
}
